/* Where each language lives in the URL.

   A crawler files one document per URL, so a page that shows Spanish or English depending on the
   visitor's browser is, to it, one English page. The Spanish copy of every PUBLIC page therefore
   has an address of its own — `/es`, `/es/how-to-play`, `/es/tutorial` — and the server writes a
   Spanish head for it (`backend:zombies_vs_humans/seo.py`, which holds the same two tables as
   this file; the two must agree). The slug stays English under `/es/` on purpose: the routes map
   one to one and nothing has to know two names for the manual.

   Only the public pages. A lobby, a game or a replay is `noindex` in any language and its address
   is generated in a dozen places; a player's language is a setting, and the URL of the room they
   are in says nothing about it. `localizedPath` leaves those alone, which is also what makes it
   safe to run on every path the app is ever on.

   The rule that ties the URL to the setting (`components/LanguageInPath`): a `/es/…` address
   *sets* the language when it is navigated to, and a change of language *moves* the address. So
   the two never disagree for longer than a render, and a Spanish player who opens `/` lands on
   `/es` — the page they were shown before this existed, now at the URL that says so. */

export const DEFAULT_LANGUAGE = "en"
export const LANGUAGE_PREFIXES = { en: "", es: "/es" }
export const PUBLIC_PATHS = ["/", "/how-to-play", "/tutorial", "/privacy", "/terms"]

const normalize = (pathname) => "/" + (pathname ?? "").replace(/^\/+|\/+$/g, "")

/** The language a path is written in, or null when it carries no prefix. `/es`, `/es/` and
 *  `/es/how-to-play` are Spanish; `/estadio` is not. */
export const languageFromPath = (pathname) => {
  const path = normalize(pathname)
  for (const [language, prefix] of Object.entries(LANGUAGE_PREFIXES)) {
    if (prefix && (path === prefix || path.startsWith(prefix + "/"))) return language
  }
  return null
}

/** The path with its language prefix taken off: what the router and the guards reason about. */
export const stripLanguage = (pathname) => {
  const path = normalize(pathname)
  const language = languageFromPath(path)
  if (!language) return path
  const prefix = LANGUAGE_PREFIXES[language]
  return normalize(path.slice(prefix.length))
}

/** Where `pathname` lives in `language`: the prefix added to a public page, taken off anything
 *  else, and an unknown language read as the default. */
export const localizedPath = (pathname, language) => {
  const bare = stripLanguage(pathname)
  const prefix = LANGUAGE_PREFIXES[language] ?? LANGUAGE_PREFIXES[DEFAULT_LANGUAGE]
  if (!prefix || !PUBLIC_PATHS.includes(bare)) return bare
  return bare === "/" ? prefix : `${prefix}${bare}`
}

/** Every address a public route answers at: its own, and one per prefixed language. */
export const routePathsFor = (path) =>
  Object.values(LANGUAGE_PREFIXES).map((prefix) => (prefix ? (path === "/" ? prefix : `${prefix}${path}`) : path))
